import Log from '@/Logger';

import { mutation, query } from './api-core';

function getEwbReqId(resp: any) {
    return resp?.headers?.['ricoh-ewb-request-id'] ?? '-';
}

type FeatureState = 'ENABLED' | 'DISABLED';

type TenantFeatureSetting = {
    id: string;
    name: string;
    state: FeatureState;

    enablingMessage?: string;
    enabledMessage?: string;
    disablingMessage?: string;
    disabledMessage?: string;
}
type Resp = {
    data: {
        tenantDevices: { deviceId: string; serialId: string; }[],
    },
    errors: any
}

type SerialIdDisplayNameMapping = {
    serialId: string;
    displayName: string;
};

// GraphQLスキーマに対応した戻り値の型定義
type UpdateDeviceDisplayNameResult = {
    isSuccess: boolean;
    message: string;
};

export type DeviceDisplayName = {
    serialIdAndDisplayNameMappings: {
        serialId: string;
        displayName: string;
    }[];
};

export async function enabledTenantFeatureSetting(id: string, state: FeatureState): Promise<TenantFeatureSetting> {
    const logger = Log.getLogger();

    logger.api.info('Lantana-API', 'Start to request: enabledTenantFeatureSetting');

    const resp = await mutation<TenantFeatureSetting>('enabledTenantFeatureSetting', 'EnabledTenantFeatureSettingtInput', { id, state }, ['id', 'name', 'state']);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute enabledTenantFeatureSetting API: id=${getEwbReqId(resp)}`;
        logger.api.error('Lantana-API', msg, resp.headers);
        throw { err: new Error(msg), id, state, };
    }

    logger.api.info('Lantana-API', 'Succeed to request: enabledTenantFeatureSetting', resp.headers);

    return resp.data.data.enabledTenantFeatureSetting;
}


export async function getSettings(): Promise<TenantFeatureSetting[]> {
    const logger = Log.getLogger();

    logger.api.info('Lantana-API', 'Start to request: allTenantFeatureSetting');

    const resp = await query<TenantFeatureSetting[]>('allTenantFeatureSetting', ['id', 'name', 'state', 'enablingMessage', 'enabledMessage', 'disablingMessage', 'disabledMessage']);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const msg = `Failed to execute allTenantFeatureSetting API: id=${getEwbReqId(resp)}`;
        logger.api.error('Lantana-API', msg, resp.headers);
        throw { err: new Error(msg), };
    }

    logger.api.info('Lantana-API', 'Succeed to request: allTenantFeatureSetting', resp.headers);

    return resp.data.data.allTenantFeatureSetting;
}

/**
 * RSIデータ認証基盤に登録されているデバイス情報を取得する関数
 * @returns { deviceId: string; serialId: string; }[] - デバイス情報の配列
 * @throws {Error} - APIリクエストが失敗した場合 
 */
export async function getDevices(): Promise<{ deviceId: string; serialId: string; }[]> {
    const logger = Log.getLogger();
    logger.api.info('Lantana-API', 'Start to request: tenantDevices');
    const resp = await query<Resp>('tenantDevices', ['deviceId', 'serialId']);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute tenantDevices API: id=${reqid}`;
        logger.api.error('Lantana-API', msg, resp.headers);
        throw new Error(msg);
    }

    logger.api.info('Lantana-API', 'Succeed to request: tenantDevices', resp.headers);

    return resp.data.data.tenantDevices;
}

/**
 * DynamoDBに登録されているデバイスの表示名を取得する関数。
 * @returns {Promise<DeviceDisplayName>} - `tenantId` と `serialIdAndDisplayNameList` を含むオブジェクト
 * @throws {Error} - APIリクエストが失敗した場合
 */
export async function getDeviceDisplayName() {
    const logger = Log.getLogger();
    logger.api.info('Lantana-API', 'Start to request: getDeviceDisplayNames');

    //GraphQLレスポンスにはdataとerrorsが含まれるので、それを考慮したAxiosのレスポンス型を定義
    const resp = await query<{ data: { deviceDisplayNames: DeviceDisplayName }, errors: any }>('deviceDisplayNames', ['serialIdAndDisplayNameMappings { serialId displayName }']);
    //もしAPIのレスポンスにエラーが含まれている場合は、エラーをスローする
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute deviceDisplayNames API: id=${reqid}`;
        logger.api.error('Lantana-API', msg, resp.headers);
        throw new Error(msg);
    }
    logger.api.info('Lantana-API', 'Succeed to request: getDeviceDisplayNames');

    // 取得したデータからdeviceDisplayNamesを返す
    // ここでのresp.data.dataは、AxiosとGraphQLのレスポンス形式に基づいている。
    return resp.data.data.deviceDisplayNames;
}

/**
 * DynamoDBに登録されているデバイスの表示名を更新する関数。
 * @returns { isSuccess: boolean; message: string; } - 更新結果を含むオブジェクト
 * @throws {Error} - APIリクエストが失敗した場合 
 */
export async function updateDeviceDisplayName(mappings: SerialIdDisplayNameMapping[]): Promise<UpdateDeviceDisplayNameResult> {
    const logger = Log.getLogger();
    logger.api.info('Lantana-API', 'Start to request: updateDeviceDisplayName');

    const resp = await mutation<UpdateDeviceDisplayNameResult>('updateDeviceDisplayName', 'DeviceDisplayNameMappingInput', { mappings }, ['isSuccess', 'message']);

    if (resp.data.errors && resp.data.errors.length > 0) {
        const msg = `Failed to execute updateDeviceDisplayName API: id=${getEwbReqId(resp)}`;
        logger.api.error('Lantana-API', msg, resp.headers);
        throw { err: new Error(msg) };
    }

    logger.api.info('Lantana-API', 'Succeed to request: updateDeviceDisplayName');

    // GraphQLレスポンスから実際のデータを返却
    return resp.data.data.updateDeviceDisplayName;
}